import IMask, { InputMask, MaskedPattern } from 'imask';
import { QR_SERVICE_API_URL } from "@modules/api/config";
import { createReview } from "@modules/api/create-review";
import { getPlatforms, type Platform } from "@modules/api/get-platforms";
import { translationService } from "@modules/intl";

let selectedRating = 0;
const path = window.location.pathname;
const branchId = path.split('/').filter(p => p !== '').pop()

const stars: NodeListOf<HTMLElement> = document.querySelectorAll('.star');
const feedbackForm = document.querySelector<HTMLDivElement>('.feedback-form');
const feedbackInput =
  document.querySelector<HTMLTextAreaElement>('.feedback-form__input');
const errorMessage =
  document.querySelector<HTMLParagraphElement>('.error-message');
const headerFeedback =
  document.querySelector<HTMLDivElement>('.header-feedback--hidden');
const headerReview = document.querySelector<HTMLDivElement>('.header-review');
const headerPlatforms =
  document.querySelector<HTMLDivElement>('.header-platforms');
const platformList = document.querySelector<HTMLDivElement>('.platforms');
const submitButton = document.querySelector<HTMLButtonElement>('.submit-btn');

const feedbackCheckbox =
  document.querySelector<HTMLInputElement>('.feedback-checkbox__input');
const contactFields = document.querySelector<HTMLDivElement>('.contact-fields');
const nameInput = document.querySelector<HTMLInputElement>('#contact-name');
const phoneInput = document.querySelector<HTMLInputElement>('#contact-phone');
const contactError =
  document.querySelector<HTMLParagraphElement>('.contact-fields__error');

type PhoneCountry = 'ru' | 'us';

const PHONE_CONFIG: Record<PhoneCountry, { code: string }> = {
  ru: { code: '7' },
  us: { code: '1' },
};

const getPhoneCountry = (): PhoneCountry => {
  return translationService.currentLang === 'ru' ? 'ru' : 'us';
};

let phoneMask: InputMask<MaskedPattern> | null = null;

const createPhoneMask = () => {
  const code = PHONE_CONFIG[getPhoneCountry()].code;
  
  if (phoneMask) {
    phoneMask.destroy();
    phoneMask = null;
  }

  if (!phoneInput) return;

  phoneMask = IMask(phoneInput, {
    mask: `+{${code}} (000) 000-00-00`,
    lazy: false,
    placeholderChar: '_',
  });
};

createPhoneMask();

const getRawPhone = (): string => {
  if (phoneMask) {
    return phoneMask.unmaskedValue;
  }
  return phoneInput?.value.replace(/\D/g, '') ?? '';
};

const clearContactFields = () => {
  if (nameInput) {
    nameInput.value = '';
    nameInput.classList.remove('error');
  }
  if (phoneInput) {
    phoneInput.value = '';
    if (phoneMask) {
      phoneMask.value = '';
    }
    phoneInput.classList.remove('error');
  }
  if (contactError) contactError.style.display = 'none';
};

phoneInput?.addEventListener('input', () => {
  const rawPhone = getRawPhone();
  if (rawPhone.length === 11) {
    phoneInput.classList.remove('error');
    if (nameInput && nameInput.value.trim().length >= 2) {
      if (contactError) contactError.style.display = 'none';
    }
  }

  if (rawPhone.length === 0) {
    phoneInput.classList.remove('error');
  }
});

feedbackCheckbox?.addEventListener('change', () => {
  const isChecked = feedbackCheckbox.checked;
  contactFields?.classList.toggle('contact-fields--hidden', !isChecked);

  if (!isChecked) {
    clearContactFields();
  }
});

const validateContactFields = (): boolean => {
  if (!nameInput || !phoneInput || !contactError) return true;

  const nameValid = nameInput.value.trim().length >= 2;
  const rawPhone = getRawPhone();
  const phoneValid = rawPhone.length === 11;

  nameInput.classList.toggle('error', !nameValid);
  phoneInput.classList.toggle('error', !phoneValid);

  const isValid = nameValid && phoneValid;
  contactError.style.display = isValid ? 'none' : 'block';

  return isValid;
};

feedbackInput?.addEventListener('input', () => {
  if (feedbackInput.value.trim().length > 0) {
    feedbackInput.classList.remove('error');
    if (errorMessage) errorMessage.style.display = 'none';
  }
});

nameInput?.addEventListener('input', () => {
  if (nameInput.value.trim().length >= 2) {
    nameInput.classList.remove('error');
    const rawPhone = getRawPhone();
    if (rawPhone.length === 11) {
      if (contactError) contactError.style.display = 'none';
    }
  }
});

const toggleStarClass = (rating: number) => {
  stars.forEach(star => {
    const starRating = parseInt(star.dataset.rating || '0');
    star.classList.toggle('star--active', starRating <= rating);
  });
};

stars.forEach(star => {
  star.addEventListener('mouseenter', () => toggleStarClass(parseInt(star.dataset.rating || '0')));
  star.addEventListener('mouseleave', () => toggleStarClass(selectedRating));
  star.addEventListener('click', () => {
    selectedRating = parseInt(star.dataset.rating || '0');
    toggleStarClass(selectedRating);

    feedbackForm?.classList.toggle('feedback-form--hidden',
      selectedRating >= 4);
  });
});

const renderPlatforms = (platforms: Platform[]) => {
  if (!platformList) return;

  platformList.innerHTML = '';

  const platformsListContainer = document.createElement('div');
  platformsListContainer.className = 'platforms__list';

  platforms.forEach(platform => {
    const platformItem = document.createElement('div');
    platformItem.className = 'platforms__list-item';
    platformItem.dataset.platform = platform.name.toLowerCase();

    const platformIcon = document.createElement('img');
    platformIcon.className = 'platforms__list-icon';

    platformIcon.src = `${QR_SERVICE_API_URL}${platform.image}`;
    platformIcon.alt = platform.name;

    platformItem.appendChild(platformIcon);

    platformItem.addEventListener('click', () => {
      window.open(platform.url, '_blank');
    });

    platformItem.title = `Оставить отзыв на ${platform.name}`;

    platformsListContainer.appendChild(platformItem);
  });

  platformList.appendChild(platformsListContainer);
};

submitButton?.addEventListener('click', async () => {
  if (!errorMessage || !feedbackInput || !submitButton) return;

  if (selectedRating === 0) {
    alert("Please, select a rating");
    return;
  }

  if (!branchId) {
    alert("Не найден id филиала");
    return;
  }

  errorMessage.style.display = 'none';
  feedbackInput.classList.remove('error');
  if (nameInput) nameInput.classList.remove('error');
  if (phoneInput) phoneInput.classList.remove('error');
  if (contactError) contactError.style.display = 'none';

  if (selectedRating < 4) {
    const comment = feedbackInput.value.trim();
    const feedbackRequested = feedbackCheckbox?.checked ?? false;

    let hasError = false;

    if (comment.length === 0) {
      feedbackInput.classList.add('error');
      errorMessage.style.display = 'block';
      hasError = true;
    }

    let name: string | null = null;
    let phone: string | null = null;

    if (feedbackRequested) {
      contactFields?.classList.remove('contact-fields--hidden');

      const isContactValid = validateContactFields();
      if (!isContactValid) {
        hasError = true;
      } else {
        name = nameInput!.value.trim();
        phone = getRawPhone();
      }
    }

    if (hasError) {
      return;
    }

    await createReview({
      branchId: branchId,
      rating: selectedRating,
      text: comment,
      feedbackRequested,
      name,
      phone,
    });

    headerReview?.classList.add('header-review--hidden');
    feedbackForm?.classList.add('feedback-form--hidden');
    submitButton.classList.add('submit-btn--hidden');
    headerFeedback?.classList.remove('header-feedback--hidden');
  } else {
    const platforms = await getPlatforms(branchId);
    renderPlatforms(platforms);

    headerReview?.classList.add('header-review--hidden');
    submitButton.classList.add('submit-btn--hidden');
    headerPlatforms?.classList.remove('header-platforms--hidden');
    platformList?.classList.remove('platforms--hidden');
  }
});
